import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface CodeBlockProps {
  code: string;
  language?: string;
  /** Display label rendered above the snippet (defaults to language). */
  label?: string;
  className?: string;
}

export function CodeBlock({ code, language = 'json', label, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — silently noop, button still gives feedback */
    }
  }

  return (
    <div
      className={cn(
        'border-foreground/10 bg-foreground text-background relative overflow-hidden rounded-xl border',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2 text-xs">
        <span className="text-background/70 font-medium uppercase tracking-[0.18em]">
          {label ?? language}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={copy}
          className="text-background/80 hover:bg-white/10 hover:text-background h-7 px-2 text-[11px] font-medium"
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="overflow-x-auto px-4 py-4 font-mono text-[13px] leading-[1.55]">
        <code>{code}</code>
      </pre>
    </div>
  );
}
